import { Component, OnInit } from '@angular/core';
import { Machine } from '@app/dtos';
import { NotificationService, PoeModalService, TranslationService, TelemetryUnitService, InputModalResult } from '@app/services';
import { ParametersService } from '@app/components/parameters/parameters.service';
import { Parameters } from '@app/components/parameters/parameters';
import { UserManagerService } from '@app/managers';
import { Router, ActivatedRoute } from '@angular/router';
import { ObservableList } from '../../types/observable-list';
import { catchError, filter, first, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { MachineInfosService } from '../machine-infos/machine-infos.service';
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerCameraDirection, CapacitorBarcodeScannerTypeHint } from '@capacitor/barcode-scanner';
import { InvalidTelemetryUnitIdError } from '@app/types/invalid-telemetry-unit-id.error';
import { InvalidTelemetryUnitPinError } from '@app/types/invalid-telemetry-unit-pin.error';
import { PlaygroundComponent } from '../playground/playground.component';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { MachinepageComponent } from '../poetpro/machinepage/machinepage.component';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { ProgressSpinnerComponent } from '../common/progress-spinner/progress-spinner.component';
import { addIcons } from 'ionicons';
import { ellipsisVertical, trashOutline } from 'ionicons/icons';
import { IonPopover, IonContent, IonChip, IonIcon, IonButton, IonLabel, IonItem, ModalController, IonList, IonAvatar, IonSpinner } from '@ionic/angular/standalone';
import { ChangeTelemetryUnitPinModalComponent } from './change-telemetry-unit-pin-modal/change-telemetry-unit-pin-modal.component';
import { FormsModule } from '@angular/forms';

@Component({
    templateUrl: './machine-details.component.html',
    styleUrls: ['./machine-details.component.scss'],
    imports: [
        PlaygroundComponent,
        TranslateModule,
        FormsModule,
        AsyncPipe,
        MachinepageComponent,
        BreadcrumbsComponent,
        ProgressSpinnerComponent,
        IonAvatar,
        IonPopover,
        IonContent,
        IonChip,
        IonIcon,
        IonButton,
        IonContent,
        IonItem,
        IonLabel,
        IonList,
        IonAvatar,
        IonSpinner,
        FormsModule
    ]
})
export class MachineDetailsComponent implements OnInit {

    params: Parameters;
    isLoading: boolean;
    isDetailsFetchRunning: boolean = true;
    embedded: boolean = true;
    machine: Machine;
    lastSavedCustomName: string;
    readonly machinePageHeader: ObservableList<string> = new ObservableList<string>();
    readonly backgrounds$ = this.machinePageHeader.asObservable();

    constructor(
        private readonly route: ActivatedRoute,
        private readonly notificationService: NotificationService,
        private readonly parameterService: ParametersService,
        private readonly machineInfosService: MachineInfosService,
        private readonly router: Router,
        private readonly userManager: UserManagerService,
        private readonly translationService: TranslationService,
        private readonly modalService: PoeModalService,
        private readonly telemetryUnitService: TelemetryUnitService,
        private readonly modalCtrl: ModalController
    ) {
        this.machineInfosService.isFetchRunning$.subscribe(x => this.isDetailsFetchRunning = x);
        addIcons({ ellipsisVertical, trashOutline });
    }

    ngOnInit() {
        this.isLoading = true;
        this.parameterService.getParametersObservable()
            .subscribe(params => {
                this.machine = params.myMachine;
            });

        const machineNumber = this.route.snapshot.url[1].path;

        if (this.machine) {
            this.lastSavedCustomName = this.machine.userDefinedName;
            this.machinePageHeader.push(`https://www.poettinger.at/img/landtechnik/produkte/phkey/2000/${this.machine?.machinephkey}.jpg`);
            this.isLoading = false;
        } else {
            this.userManager.getUserMachine(machineNumber).pipe(first())
                .subscribe({
                    next: machineMinimal => {
                        this.machine = machineMinimal;
                        this.parameterService.setMyMachine(machineMinimal);
                        this.lastSavedCustomName = this.machine.userDefinedName;
                        this.machinePageHeader.push(`https://www.poettinger.at/img/landtechnik/produkte/phkey/2000/${this.machine?.machinephkey}.jpg`);
                        this.isLoading = false;
                    },
                    error: e => console.error(e)
                });
        }
    }

    onMouseMove(event: MouseEvent) {
        const popover = event.target as unknown as (IonPopover & HTMLElement);
        if (!popover.trigger) {
            return;
        }
        const trigger = document.getElementById(popover.trigger);
        if (!trigger) {
            return;
        }
        const rect = trigger.getBoundingClientRect();
        const insideTrigger =
            event.clientX >= rect.left - 10 &&
            event.clientX <= rect.right + 10 &&
            event.clientY >= rect.top - 10 &&
            event.clientY <= rect.bottom + 10;
        if (insideTrigger) {
            return;
        }
        const popoverViewport = popover.querySelector('.popover-viewport');
        if (!popoverViewport) {
            return;
        }
        const popoverRect = popoverViewport.getBoundingClientRect();
        const insidePopover =
            event.clientX >= popoverRect.left - 10 &&
            event.clientX <= popoverRect.right + 10 &&
            event.clientY >= popoverRect.top - 10 &&
            event.clientY <= popoverRect.bottom + 10;
        if (!insidePopover) {
            popover.dismiss();
        }
    }

    async openChangeTelemetryUnitPinModal() {
        const modal = await this.modalCtrl.create({
            component: ChangeTelemetryUnitPinModalComponent,
            componentProps: {
                telemetryUnitId: this.machine.connectedTelemetryUnitId ?? ''
            },
            cssClass: 'pin-modal-auto-height'
        });
        modal.present();

        await modal.onWillDismiss();
    }

    onSaveMachineName(machine: Machine) {
        this.isLoading = true;
        this.userManager.updateUserMachine(machine.machineNumber, {
            sort: machine.sort,
            userDefinedName: machine.userDefinedName
        }).pipe(
            switchMap(() => this.userManager.getUserMachine(machine.machineNumber))
        ).subscribe({
            next: (data: Machine) => {
                this.machine = data;
                this.lastSavedCustomName = data.userDefinedName;
                this.notificationService.notifySavedSuccessfully();
                this.isLoading = false;
                this.router.navigate(['/myMachines/']);
            },
            error: _ => {
                this.notificationService.notifyErrorWithTranslate('ErrorLoadingData');
                this.isLoading = false;
            }
        });
    }

    onDeleteUserMachine(machine: Machine) {
        this.modalService.showConfirmationModal({
            title: this.translationService.translateInstant('removeMachine'),
            message: this.translationService.translateInstant('reallyRemoveMachine'),
            confirmText: this.translationService.translateInstant('permanentlyDelete'),
            cancelText: this.translationService.translateInstant('back')
        }).subscribe(reason => {
            if (reason) {
                this.doDelete(machine);
            }
        });
    }

    doDelete(machine: Machine): void {
        this.isLoading = true;
        this.userManager.deleteUserMachine(machine.id).subscribe({
            next: () => {
                this.notificationService.notifyDeletedSuccessfully();
                this.isLoading = false;
                this.router.navigate(['/myMachines/']);
            },
            error: _ => {
                this.notificationService.notifyErrorWithTranslate('ErrorLoadingData');
                this.isLoading = false;
            }
        });
    }

    linkTelemetryUnit(): void {
        of(this.machine).pipe(
            map(machine => machine.suggestedTelemetryUnitId),
            catchError(_err => this.getTelemetryUnitIdFromQrCode()),
            tap(telemetryUnitId => {
                if (!/^\d+$/.test(telemetryUnitId)) {
                    throw new InvalidTelemetryUnitIdError();
                }
            }),
            switchMap(telemetryUnitId =>
                this.promptForTelemetryUnitPin(telemetryUnitId)
                    .pipe(map(modalResult => ({ wasDismissed: modalResult.wasDismissed, pinCode: modalResult.value, telemetryUnitId })))
            ),
            filter(({ wasDismissed }) => !wasDismissed),
            tap(({ pinCode }) => {
                if (!/^.{4,}$/.test(pinCode ?? '')) {
                    throw new InvalidTelemetryUnitPinError();
                }
            }),
            switchMap(({ telemetryUnitId, pinCode }) =>
                this.telemetryUnitService.linkTelemetryUnit(telemetryUnitId, this.machine.sernr, pinCode)
                    .pipe(map(() => telemetryUnitId))
            )
        ).subscribe({
            next: telemetryUnitId => {
                this.machine.connectedTelemetryUnitId = telemetryUnitId;
                this.machine.suggestedTelemetryUnitId = undefined;
            },
            error: err => {
                console.error(err);
                if (err instanceof InvalidTelemetryUnitIdError) {
                    this.notificationService.notifyErrorWithTranslate('invalidTelemetryUnit');
                    return;
                }
                if (err instanceof InvalidTelemetryUnitPinError || err.status === 403) {
                    this.notificationService.notifyErrorWithTranslate('invalid-pin-code');
                }
            }
        });
    }

    private promptForTelemetryUnitPin(telemetryUnitId: string): Observable<InputModalResult> {
        return this.modalService.showInputModal({
            title: this.translationService.translateInstant('linkTelemetryUnit'),
            message: this.translationService.translateInstant('linkTelemetryUnitMessage', { telemetryUnitId }),
            confirmText: this.translationService.translateInstant('ok'),
            cancelText: this.translationService.translateInstant('Cancel')
        }, {
            centered: true
        });
    }

    private getTelemetryUnitIdFromQrCode(): Observable<string> {
        return from(CapacitorBarcodeScanner.scanBarcode({
            cameraDirection: CapacitorBarcodeScannerCameraDirection.FRONT,
            hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
            scanButton: true,
            scanInstructions: 'Test',
            scanText: 'ScanText',
            web: {
                showCameraSelection: true
            }
        })).pipe(
            filter(result => !!result.ScanResult),
            map(result => result.ScanResult)
        );
    }
}
